// src/index.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text
} from "@chakra-ui/react";
import {
  components,
  Select
} from "chakra-react-select";
import { Info } from "@airhelp/icons";
import { ErrorIconColor } from "@airhelp/input";
import { theme } from "@airhelp/chakra-theme";
import { jsx, jsxs } from "react/jsx-runtime";
var getAutocompleteIcon = (iconColor, icon) => {
  return (dropdownIndicatorProps) => icon ? /* @__PURE__ */ jsx(components.DropdownIndicator, {
    ...dropdownIndicatorProps,
    children: /* @__PURE__ */ jsx(InputLeftElement, {
      color: iconColor,
      height: "46px",
      children: icon
    })
  }) : null;
};
var getLoadingIndicator = () => () => /* @__PURE__ */ jsx(Spinner, {
  size: "xs",
  position: "absolute",
  right: 4
});
var getSingleValue = () => (singleValueProps) => /* @__PURE__ */ jsxs(components.SingleValue, {
  ...singleValueProps,
  children: [
    singleValueProps.data.value.name,
    ", ",
    singleValueProps.data.value.iata
  ]
});
var AirportAutocomplete = ({
  icon,
  label,
  caption,
  isInvalid,
  colorScheme,
  dataTestId,
  handleChange,
  errorMessage,
  formControlStyles,
  ...props
}) => {
  const [errorIconColor, setErrorIconColor] = useState(
    colorScheme === "warning" ? ErrorIconColor.warning : ErrorIconColor.danger
  );
  const [iconColor, setIconColor] = useState("greyscale.500");
  const [isAutocompleteFocused, setIsAutocompleteFocused] = useState(false);
  const reactSelectStyles = useMemo(
    () => icon ? theme.components.getAutocompleteWithIconStyles() : theme.components.getAutocompleteStyles(),
    [icon]
  );
  useEffect(() => {
    setErrorIconColor(colorScheme === "warning" ? ErrorIconColor.warning : ErrorIconColor.danger);
  }, [colorScheme]);
  const onFocus = () => {
    setIconColor("primary.500");
    setErrorIconColor("transparent");
    setIsAutocompleteFocused(true);
  };
  const onBlur = () => {
    setIconColor("greyscale.500");
    setErrorIconColor(colorScheme === "warning" ? ErrorIconColor.warning : ErrorIconColor.danger);
    setIsAutocompleteFocused(false);
  };
  const onMouseOver = () => {
    if (!isAutocompleteFocused) {
      setErrorIconColor(colorScheme === "warning" ? ErrorIconColor.warningHovered : ErrorIconColor.dangerHovered);
    }
  };
  const onMouseOut = () => {
    if (!isAutocompleteFocused) {
      setErrorIconColor(colorScheme === "warning" ? ErrorIconColor.warning : ErrorIconColor.danger);
    }
  };
  const formatOptionLabel = (option) => /* @__PURE__ */ jsxs(Box, {
    children: [
      /* @__PURE__ */ jsxs(Text, {
        fontSize: "md",
        color: "none",
        children: [
          option.value.name,
          ", ",
          option.value.iata
        ]
      }),
      /* @__PURE__ */ jsxs(Text, {
        fontSize: "sm",
        color: "none",
        children: [
          option.value.city,
          ", ",
          option.value.country
        ]
      })
    ]
  });
  return /* @__PURE__ */ jsxs(FormControl, {
    ...formControlStyles,
    alignItems: "start",
    isInvalid,
    children: [
      label && /* @__PURE__ */ jsx(FormLabel, {
        fontWeight: "bold",
        children: label
      }),
      /* @__PURE__ */ jsx(InputGroup, {
        onFocus,
        onBlur,
        onMouseOver,
        onMouseOut,
        "data-testid": dataTestId,
        isolation: "unset",
        children: /* @__PURE__ */ jsx(Select, {
          colorScheme,
          isInvalid,
          onChange: handleChange,
          noOptionsMessage: () => null,
          loadingMessage: () => null,
          openMenuOnFocus: false,
          openMenuOnClick: false,
          backspaceRemovesValue: true,
          isClearable: true,
          isSearchable: true,
          blurInputOnSelect: true,
          isOptionDisabled: (option) => Boolean(option.isDisabled),
          closeMenuOnSelect: true,
          filterOption: () => true,
          formatOptionLabel,
          ...props,
          chakraStyles: reactSelectStyles,
          components: {
            DropdownIndicator: getAutocompleteIcon(iconColor, icon),
            LoadingIndicator: getLoadingIndicator(),
            ClearIndicator: () => null,
            SingleValue: getSingleValue()
          }
        })
      }),
      isInvalid && /* @__PURE__ */ jsx(InputGroup, {
        children: /* @__PURE__ */ jsx(InputRightElement, {
          right: 1,
          top: "unset",
          bottom: "1px",
          pointerEvents: "none",
          children: /* @__PURE__ */ jsx(Info, {
            boxSize: 4,
            pointerEvents: "none",
            color: errorIconColor
          })
        })
      }),
      /* @__PURE__ */ jsx(FormErrorMessage, {
        colorScheme,
        children: errorMessage
      }),
      caption && !isInvalid && /* @__PURE__ */ jsx(FormHelperText, {
        children: caption
      })
    ]
  });
};
export {
  AirportAutocomplete
};
